import React, { useEffect, useState, useMemo, useCallback } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/TopNav";
import { PageHeader, Delta, EmptyState, HalalBadge, LiveBadge } from "@/components/common";
import { MarketsTabs } from "@/components/markets/MarketsTabs";
import { TradeSheet } from "@/components/order/TradeSheet";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { fmtUSD, timeAgo } from "@/lib/format";
import { Search, Star, Plus, Warehouse } from "lucide-react";
import { toast } from "sonner";
import { toastError } from "@/lib/toastError";

/**
 * Generic trading page for alternative asset classes (commodities / forex / crypto).
 * Props: assetClass ("commodity"|"forex"|"crypto"), title, subtitle, unit.
 */
export default function TradeAssets({ assetClass, title, subtitle, unit }) {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [liveInfo, setLiveInfo] = useState({ live_count: 0, total: 0 });
  const [sector, setSector] = useState("All");
  const [search, setSearch] = useState("");
  const [watchlist, setWatchlist] = useState([]);
  const [orders, setOrders] = useState([]);
  const [ticket, setTicket] = useState({ open: false, symbol: null });
  const [custody, setCustody] = useState(null);

  const loadList = useCallback(async () => {
    const { data } = await api.get(`/market/live/${assetClass}`);
    setList(data.data); setLiveInfo({ live_count: data.live_count, total: data.total });
  }, [assetClass]);
  const loadSide = useCallback(async () => {
    const [wl, ord] = await Promise.all([api.get("/watchlist"), api.get(`/orders?source=${assetClass}`)]);
    setWatchlist(wl.data.filter((s) => s.asset_class === assetClass)); setOrders(ord.data);
    if (assetClass === "commodity") {
      try { const { data } = await api.get("/custody/summary"); setCustody(data); } catch (e) { console.error("Custody summary unavailable:", e); }
    }
  }, [assetClass]);
  useEffect(() => { loadList(); loadSide(); }, [loadList, loadSide]);
  useEffect(() => { const iv = setInterval(loadList, 20000); return () => clearInterval(iv); }, [loadList]);

  const sectors = useMemo(() => ["All", ...Array.from(new Set(list.map((s) => s.sector)))], [list]);
  const filtered = list.filter((s) =>
    (sector === "All" || s.sector === sector) &&
    (!search || s.symbol.toLowerCase().includes(search.toLowerCase()) || s.name.toLowerCase().includes(search.toLowerCase())));

  const addWatch = async (symbol) => { try { await api.post("/watchlist", { symbol }); toast.success(`${symbol} added to watchlist`); loadSide(); } catch (e) { toastError("Couldn't add to watchlist", e); } };

  const priceLabel = (s) => assetClass === "forex" ? s.price?.toFixed(4) : fmtUSD(s.price);

  return (
    <AppShell>
      <PageHeader title={title} subtitle={subtitle}
        action={<div className="flex items-center gap-2">
          {liveInfo.live_count > 0 && <Badge className="text-[10px]" data-testid="alt-live-badge">{liveInfo.live_count} live · real-time</Badge>}
          <LiveBadge />
        </div>} testid="alt-header" />

      <MarketsTabs />

      <Tabs value={sector} onValueChange={setSector} className="mb-4" data-testid="alt-sector-tabs">
        <TabsList className="flex-wrap h-auto">{sectors.map((s) => <TabsTrigger key={s} value={s} data-testid={`alt-sector-${s}`}>{s}</TabsTrigger>)}</TabsList>
      </Tabs>

      <div className="grid gap-4 sm:gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4 sm:space-y-5">
          <Card className="p-4 shadow-premium">
            <div className="relative mb-3">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder={`Search ${title.toLowerCase()}...`} value={search} onChange={(e) => setSearch(e.target.value)} className="ps-9" data-testid="alt-search-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-2 max-h-[520px] overflow-auto" data-testid="alt-price-cards">
              {filtered.map((s) => (
                <div key={`ta-${s.symbol}`} className="rounded-lg border p-3 hover:bg-accent transition-colors cursor-pointer group"
                  onClick={() => navigate(`/stock/${encodeURIComponent(s.symbol)}`)}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium flex items-center gap-2">{s.name} {s.halal && <HalalBadge />}</div>
                      <div className="text-xs text-muted-foreground">{s.symbol} · {s.sector}</div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100"
                      onClick={(e) => { e.stopPropagation(); addWatch(s.symbol); }} data-testid={`alt-watch-${s.symbol}`}><Star className="h-4 w-4" /></Button>
                  </div>
                  <div className="flex items-end justify-between mt-2">
                    <div>
                      <span className="numeric text-lg font-semibold">{priceLabel(s)}</span>
                      {s.unit && <span className="text-[11px] text-muted-foreground ms-1">{s.unit}</span>}
                      {s.source && s.source !== "simulated" && <span className="ms-2 inline-flex items-center gap-1 text-[10px] text-emerald-600"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 live-dot" />live</span>}
                    </div>
                    <Delta value={s.change_pct} className="text-sm" />
                  </div>
                  <Button size="sm" className="w-full mt-2" onClick={(e) => { e.stopPropagation(); setTicket({ open: true, symbol: s.symbol }); }} data-testid={`alt-trade-${s.symbol}`}>Trade</Button>
                </div>
              ))}
              {!filtered.length && <div className="sm:col-span-2"><EmptyState title="No matches" hint="Try another search or category" /></div>}
            </div>
          </Card>

          <Card className="p-4 shadow-premium">
            <div className="text-sm font-medium mb-3">{title} Order History</div>
            {orders.length ? (
              <Table data-testid="alt-orders-table"><TableHeader><TableRow><TableHead>Symbol</TableHead><TableHead>Side</TableHead><TableHead className="text-end">Qty</TableHead><TableHead className="text-end">Price</TableHead><TableHead className="text-end">Time</TableHead></TableRow></TableHeader>
                <TableBody>{orders.slice(0, 10).map((o) => (<TableRow key={o.id}>
                  <TableCell className="font-medium">{o.symbol}</TableCell>
                  <TableCell><Badge variant={o.side === "buy" ? "default" : "destructive"} className="capitalize">{o.side}</Badge></TableCell>
                  <TableCell className="text-end numeric">{o.qty}</TableCell>
                  <TableCell className="text-end numeric">{fmtUSD(o.fill_price)}</TableCell>
                  <TableCell className="text-end text-xs text-muted-foreground">{timeAgo(o.timestamp)}</TableCell>
                </TableRow>))}</TableBody></Table>
            ) : <EmptyState title="No orders yet" hint={`Your ${title.toLowerCase()} trades appear here`} />}
          </Card>
        </div>

        <div className="space-y-4 sm:space-y-5">
          {assetClass === "commodity" && custody && (
            <Card className="p-4 shadow-premium" data-testid="custody-card">
              <div className="flex items-center gap-2 text-sm font-medium mb-1">
                <Warehouse className="h-4 w-4 text-[color:var(--tharwy-lime)]" />Where your commodities sit
              </div>
              {custody.holdings.length ? (
                <>
                  <p className="text-xs text-muted-foreground mb-3">
                    Storage &amp; insurance is <span className="numeric font-semibold text-foreground" data-testid="custody-monthly-total">{fmtUSD(custody.monthly_total_usd)}</span> a month
                    ({fmtUSD(custody.annual_total_usd)} a year), collected from your USD wallet.
                  </p>
                  <div className="space-y-2">
                    {custody.holdings.map((h) => (
                      <div key={`${h.symbol}-custody`} className="rounded-lg border px-3 py-2" data-testid={`custody-holding-${h.symbol}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-medium text-sm">{h.name}</div>
                          <div className="numeric text-sm">{fmtUSD(h.market_value_usd)}</div>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {h.qty} {h.unit} · {h.location}
                        </div>
                        <div className="text-xs mt-0.5">
                          {h.waived ? (
                            <span className="text-emerald-600 font-medium">Storage free under {fmtUSD(h.waiver_usd)}</span>
                          ) : (
                            <span className="numeric text-muted-foreground">{fmtUSD(h.monthly_cost_usd)} / month · {h.monthly_rate_pct}%</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Buy a commodity and we'll show you the exchange-approved warehouse or vault holding it,
                  and what it costs to keep it there each month.
                </p>
              )}
            </Card>
          )}

          <Card className="p-4 shadow-premium" data-testid="alt-watchlist">
            <div className="flex items-center gap-2 text-sm font-medium mb-3"><Star className="h-4 w-4 text-[color:var(--tharwy-lime)]" />Watchlist</div>
            {watchlist.length ? (
              <div className="space-y-2">{watchlist.map((s) => (
                <div key={`tawl-${s.symbol}`} className="flex items-center justify-between rounded-lg border px-3 py-2 hover:bg-accent transition-colors cursor-pointer"
                  onClick={() => navigate(`/stock/${encodeURIComponent(s.symbol)}`)}>
                  <div><div className="font-medium text-sm">{s.name}</div><div className="text-xs text-muted-foreground">{s.symbol}</div></div>
                  <div className="text-end"><div className="numeric text-sm">{priceLabel(s)}</div><Delta value={s.change_pct} className="text-xs" /></div>
                </div>
              ))}</div>
            ) : <EmptyState title="Empty watchlist" hint="Star an instrument to track it" />}
          </Card>
        </div>
      </div>

      <TradeSheet open={ticket.open} onOpenChange={(v) => setTicket({ ...ticket, open: v })} symbol={ticket.symbol} source={assetClass} onDone={loadSide} />
    </AppShell>
  );
}
